// Dashboard Component - Extracted from App.js for better maintainability
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import axiosClient from '../utils/axiosClient';

// Import custom components
import FilePreview from './FilePreview';
import { TaskList, NotificationBell } from './TaskManager';
import BabelmixLogo from './BabelmixLogo';
import UserProfile from './UserProfile';
import OnboardingTutorial, { shouldShowOnboarding } from './help/OnboardingTutorial';
import HelpPage from './help/HelpPage';
import LegalPage from './LegalPage';

// Import utilities
import { getApiUrl } from '../utils/api';

// Import Shadcn components
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback } from './ui/avatar';

// Icons
import { MessageCircle, Users, Home, X, ChevronRight, Info, ClipboardList, FileSpreadsheet, FileText, FileArchive, Hand, HelpCircle, Table, Scale } from 'lucide-react';
import PWAInstall from './PWAInstall';

// Import debug components
import { MobileInstallButton } from './debug';

// Import project components
import { ProjectInfo, ProjectTree, ProjectMembers, ProjectFiles, ProjectDataTables } from './projects';

// Import chat components
import { MainChat, Chat } from './chat';

// Import Modals
import ImportModal from './ImportModal';
import BCFImportModal from './BCFImportModal';

const Dashboard = ({ currentUser, onLogout, justRegistered = false, onOpenFeldModus }) => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [projectsHierarchy, setProjectsHierarchy] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [activeTab, setActiveTab] = useState('chat');
  const [showMainChat, setShowMainChat] = useState(true);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showLegal, setShowLegal] = useState(false);
  
  // User Profile Edit States
  const [profileEditMode, setProfileEditMode] = useState(false);
  const [profileUserData, setProfileUserData] = useState({
    name: currentUser?.name || '',
    company: currentUser?.company || '',
    phone_number: currentUser?.phone_number || '',
    language: currentUser?.language || 'de'
  });
  const [profileSaving, setProfileSaving] = useState(false);
  
  // Reset profile data when opening profile page
  useEffect(() => {
    if (showUserProfile) {
      setProfileUserData({
        name: currentUser?.name || '',
        company: currentUser?.company || '',
        phone_number: currentUser?.phone_number || '',
        language: currentUser?.language || 'de'
      });
      setProfileEditMode(false);
    }
  }, [showUserProfile, currentUser]);

  // Show onboarding for first-time users
  useEffect(() => {
    if (justRegistered || shouldShowOnboarding()) {
      // Small delay to let the dashboard load first
      const timer = setTimeout(() => {
        setShowOnboarding(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [justRegistered]);
  
  // Scroll behavior state
  const [scrollToMessage, setScrollToMessage] = useState(null);
  const [scrollBehavior, setScrollBehavior] = useState('bottom');
  
  // File Preview State
  const [filePreview, setFilePreview] = useState({
    isOpen: false,
    fileUrl: '',
    fileName: '',
    fileType: '',
    originalFileUrl: ''
  });

  // Join Project with Code State
  const [showJoinCodeModal, setShowJoinCodeModal] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState('');

  // Invite System State
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  
  // Import Modal States
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBCFImportModal, setShowBCFImportModal] = useState(false);
  
  // Project Members State for Tasks
  const [projectMembers, setProjectMembers] = useState([]);

  // Mobile detection hook
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const loadUserProjects = useCallback(async () => {
    if (!currentUser?.id) {
      console.log('No current user, cannot load projects');
      return;
    }
    
    try {
      const response = await axiosClient.get(`/projects/user/${currentUser.id}`);
      const projectsData = response.data;
      
      const projectMap = {};
      const rootProjects = [];
      
      projectsData.forEach(project => {
        projectMap[project.id] = { ...project, children: [] };
      });
      
      projectsData.forEach(project => {
        if (project.parent_id && projectMap[project.parent_id]) {
          projectMap[project.parent_id].children.push(projectMap[project.id]);
        } else {
          rootProjects.push(projectMap[project.id]);
        }
      });
      
      const flatProjects = [];
      const addProjectsRecursively = (projectList) => {
        projectList.forEach(project => {
          flatProjects.push(project);
          if (project.children && project.children.length > 0) {
            addProjectsRecursively(project.children);
          }
        });
      };
      addProjectsRecursively(rootProjects);
      
      setProjects(flatProjects);
      setProjectsHierarchy(rootProjects);
    } catch (error) {
      if (!error.response || ![500, 502, 520].includes(error.response?.status)) {
        console.error('Error loading projects:', error);
      }
    }
  }, [currentUser?.id]);

  useEffect(() => {
    if (currentUser) {
      loadUserProjects();
      
      const projectsInterval = setInterval(() => {
        loadUserProjects();
      }, 30000);
      
      return () => {
        clearInterval(projectsInterval);
      };
    }
  }, [currentUser, loadUserProjects]);

  // Load project members when project changes
  useEffect(() => {
    const loadProjectMembers = async () => {
      if (!currentProject?.id) {
        setProjectMembers([]);
        return;
      }
      try {
        const response = await axios.get(`${getApiUrl()}/projects/${currentProject.id}/members`);
        setProjectMembers(response.data || []);
      } catch (error) {
        console.error('Error loading project members for tasks:', error);
        setProjectMembers([]);
      }
    };
    loadProjectMembers();
  }, [currentProject?.id]);

  const handleProjectSelect = (project) => {
    setCurrentProject(project);
    setShowMainChat(false);
    setActiveTab('chat');
    setScrollToMessage(null);
    setScrollBehavior('none');
  };

  const handleBackToMainChat = () => {
    setCurrentProject(null);
    setShowMainChat(true);
  };

  const handleMessageClick = React.useCallback((message) => {
    const project = projects.find(p => p.id === message.project_id);
    
    if (project) {
      setCurrentProject(project);
      setShowMainChat(false);
      setActiveTab('chat');
      setScrollToMessage(null);
      setScrollBehavior('none');
    } else {
      console.error('Project not found for message:', message.project_id);
    }
  }, [projects]);

  const handleTaskClick = React.useCallback((task) => {
    const project = projects.find(p => p.id === task.project_id);
    
    if (project) {
      setCurrentProject(project);
      setShowMainChat(false);
      setActiveTab('tasks');
    } else {
      console.error('Project not found for task:', task.project_id);
    }
  }, [projects]);

  // Join Project with Invite Code
  const joinWithCode = async () => {
    if (!joinCode.trim()) {
      setJoinError(t('joinCode.enterCode'));
      return;
    }
    
    setJoinLoading(true);
    setJoinError('');
    
    try {
      const response = await axios.post(`${getApiUrl()}/invite/${joinCode.trim().toUpperCase()}/join`, null, {
        params: { user_id: currentUser.id }
      });
      
      // Success - reload projects to show newly joined project
      await loadUserProjects();
      setShowJoinCodeModal(false);
      setJoinCode('');
      alert(t('joinCode.success'));
    } catch (error) {
      console.error('Error joining with code:', error);
      if (error.response?.status === 404) {
        setJoinError(t('joinCode.invalidCode'));
      } else if (error.response?.status === 400) {
        setJoinError(error.response?.data?.detail || t('joinCode.expired'));
      } else {
        setJoinError(t('joinCode.error'));
      }
    } finally {
      setJoinLoading(false);
    }
  };

  // Generate Invite Link
  const generateInviteLink = async (projectId = null) => {
    try {
      const response = await axios.post(`${getApiUrl()}/generate-invite`, {
        creator_id: currentUser.id,
        project_id: projectId,
        company: currentUser.company || 'Babelmix Team'
      });
      setInviteLink(response.data.invite_link);
      setShowInviteModal(true);
    } catch (error) {
      console.error('Error generating invite:', error);
      alert(t('alerts.inviteCreateError'));
    }
  };

  // Copy invite link to clipboard
  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink).then(() => {
      alert(t('alerts.linkCopied'));
    }).catch(() => {
      alert(t('alerts.linkCopyError'));
    });
  };

  // File Preview Handlers
  const openFilePreview = (fileUrl, fileName, fileType, originalFileUrl = null) => {
    const detectedType = getFileTypeFromFilename(fileName);
    
    setFilePreview({
      isOpen: true,
      fileUrl,
      fileName,
      fileType: fileType || detectedType,
      originalFileUrl: originalFileUrl || null
    });
  };

  const closeFilePreview = () => {
    setFilePreview({
      isOpen: false,
      fileUrl: '',
      fileName: '',
      fileType: '',
      originalFileUrl: ''
    });
  };

  const getFileTypeFromFilename = (filename) => {
    if (!filename) return 'unknown';
    const extension = filename.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) return 'image';
    return 'unknown';
  };

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const handleCreateProject = (newProject) => {
    setProjects(prev => [...prev, { ...newProject, children: [] }]);
    setTimeout(() => {
      loadUserProjects();
    }, 500);
  };

  const buildBreadcrumbPath = (project, allProjects) => {
    const path = [];
    let currentProj = project;
    
    while (currentProj) {
      path.unshift(currentProj);
      
      if (currentProj.parent_id) {
        const findProject = (projects, id) => {
          for (const proj of projects) {
            if (proj.id === id) return proj;
            const found = findProject(proj.children || [], id);
            if (found) return found;
          }
          return null;
        };
        currentProj = findProject(allProjects, currentProj.parent_id);
      } else {
        currentProj = null;
      }
    }
    
    return path;
  };

  // Show User Profile if active
  if (showUserProfile) {
    return (
      <UserProfile 
        currentUser={currentUser}
        onClose={() => setShowUserProfile(false)}
        onLogout={onLogout}
        profileEditMode={profileEditMode}
        setProfileEditMode={setProfileEditMode}
        profileUserData={profileUserData}
        setProfileUserData={setProfileUserData}
        profileSaving={profileSaving}
        setProfileSaving={setProfileSaving}
      />
    );
  }

  return (
    <div 
      className="flex flex-col relative" 
      style={{ 
        height: '100dvh',
        minHeight: '100dvh',
        maxHeight: '100dvh',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        backgroundColor: '#f3f4f6'
      }}
    >
      {/* Onboarding Tutorial for first-time users */}
      {showOnboarding && (
        <OnboardingTutorial 
          currentUser={currentUser}
          onComplete={() => setShowOnboarding(false)}
        />
      )}

      {/* Help Page Modal */}
      {showHelp && (
        <HelpPage 
          onClose={() => setShowHelp(false)} 
          currentUser={currentUser}
        />
      )}

      {/* Legal Page Modal */}
      {showLegal && (
        <LegalPage 
          isOpen={showLegal}
          onClose={() => setShowLegal(false)} 
        />
      )}

      {/* Main Content Container */}
      <div className="flex flex-1 relative" style={{ minHeight: 0, overflow: 'hidden', backgroundColor: 'white' }}>
      {/* Sidebar - Overlay when visible */}
      <div className={`${sidebarVisible ? 'w-80 opacity-100' : 'w-0 opacity-0'} 
        transition-all duration-300 bg-white shadow-lg fixed inset-y-0 left-0 z-50 overflow-hidden flex flex-col`}
        style={{ maxHeight: '100vh', height: '100vh' }}>
        {sidebarVisible && (
          <>
            {/* Sidebar Header */}
            <div className="px-3 py-2 border-b flex-shrink-0">
              <div className="flex items-center justify-between">
                <BabelmixLogo size="small" />
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={toggleSidebar} className="h-7 w-7 p-0">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2">
                <button 
                  onClick={() => { setShowUserProfile(true); setSidebarVisible(false); }}
                  className="flex items-center flex-1 min-w-0 mr-2 p-1 rounded-lg hover:bg-emerald-50 transition-colors text-left"
                >
                  <Avatar className="w-7 h-7 mr-2">
                    <AvatarFallback className="bg-emerald-500 text-white text-xs">
                      {currentUser.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{currentUser.name}</p>
                    <p className="text-xs text-gray-500 truncate">{currentUser.company}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowJoinCodeModal(true)}
                  className="text-xs h-7 px-2 flex-shrink-0"
                >
                  🔗 {t('sidebar.joinProject')}
                </Button>
              </div>
              
              {/* Feld-Modus Button */}
              {onOpenFeldModus && (
                <Button
                  onClick={() => {
                    setSidebarVisible(false);
                    onOpenFeldModus();
                  }}
                  className="w-full h-14 mt-2 bg-amber-600 hover:bg-amber-700 text-white text-lg font-bold"
                >
                  <Hand className="w-6 h-6 mr-2" /> {t('sidebar.feldModus', 'FELD-MODUS')}
                </Button>
              )}
              
              <div className="mt-2">
                <MobileInstallButton />
              </div>
            </div>

            {/* Main Chat Button + Import Button */}
            <div className="px-3 py-2 border-b space-y-2 flex-shrink-0">
              <Button
                onClick={() => {
                  handleBackToMainChat();
                  setSidebarVisible(false);
                }}
                variant={showMainChat ? "default" : "outline"}
                className="w-full justify-start h-9 text-sm"
              >
                <Home className="w-4 h-4 mr-2" />
                {t('dashboard.allMessages')}
              </Button>
              
              {/* Import Buttons */}
              <div className="space-y-1">
                <Button
                  onClick={() => {
                    setShowImportModal(true);
                    setSidebarVisible(false);
                  }}
                  variant="outline"
                  className="w-full justify-start h-9 text-sm border-dashed border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  {t('sidebar.import')}
                </Button>
                
                <Button
                  onClick={() => {
                    setShowBCFImportModal(true);
                    setSidebarVisible(false);
                  }}
                  variant="outline"
                  className="w-full justify-start h-9 text-sm border-dashed border-orange-300 text-orange-700 hover:bg-orange-50"
                >
                  <FileArchive className="w-4 h-4 mr-2" />
                  BCF Import
                </Button>
              </div>
            </div>

            {/* Projects Section */}
            <div className="flex-1 min-h-0 px-3 py-2 overflow-y-auto pb-20">
              <div className="space-y-2">
                <ProjectTree 
                  projects={projectsHierarchy} 
                  currentProject={currentProject}
                  onProjectSelect={(project) => {
                    handleProjectSelect(project);
                    setSidebarVisible(false);
                  }}
                  onCreateProject={handleCreateProject}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Sidebar Backdrop */}
      {sidebarVisible && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={toggleSidebar}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 w-full">
        {showMainChat ? (
          <div className="h-full flex flex-col bg-white">
            {/* Header */}
            <div className="p-3 bg-emerald-600 text-white border-b flex-shrink-0 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <button 
                    onClick={toggleSidebar}
                    style={{
                      marginRight: '12px',
                      padding: '8px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '20px'
                    }}
                  >
                    📁
                  </button>
                  <h1 className="text-xl font-bold text-white">{t('dashboard.allMessages')}</h1>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowLegal(true)}
                    className="hover:bg-emerald-700"
                    title={t('legal.title', 'Rechtliches')}
                    data-testid="header-legal-button"
                  >
                    <Scale className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowHelp(true)}
                    className="hover:bg-emerald-700"
                    title={t('help.title', 'Hilfe')}
                    data-testid="header-help-button-main"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowUserProfile(true)}
                    className="flex items-center gap-1 hover:bg-emerald-700"
                  >
                    <Avatar className="w-6 h-6">
                      <AvatarFallback className="bg-white text-emerald-600 text-xs font-bold">
                        {currentUser.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="flex-1 min-h-0">
              <MainChat 
                currentUser={currentUser}
                onMessageClick={handleMessageClick}
                onTaskClick={handleTaskClick}
                onBackToProjects={handleBackToMainChat}
                onFilePreview={openFilePreview}
              />
            </div>
          </div>
        ) : currentProject ? (
          <div className="h-full flex flex-col bg-white">
            {/* Project Header */}
            <div className={`bg-emerald-600 text-white border-b flex-shrink-0 shadow-sm ${isMobile ? 'py-1 px-2' : 'py-2 px-4'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <button 
                    onClick={toggleSidebar}
                    style={{
                      marginRight: '6px',
                      padding: isMobile ? '2px' : '4px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: isMobile ? '16px' : '18px'
                    }}
                  >
                    📁
                  </button>
                  
                  {/* Breadcrumb Path */}
                  <div className={`flex items-center font-semibold text-gray-800 ${isMobile ? 'text-sm' : 'text-base'}`}>
                    {(() => {
                      const breadcrumbPath = buildBreadcrumbPath(currentProject, projects);
                      return breadcrumbPath.map((project, index) => (
                        <div key={project.id} className="flex items-center">
                          <button 
                            onClick={() => setCurrentProject(project)}
                            className={`hover:text-emerald-600 transition-colors ${
                              index === breadcrumbPath.length - 1 
                                ? 'text-gray-800 cursor-default' 
                                : 'text-gray-600 hover:underline'
                            }`}
                          >
                            {project.name}
                          </button>
                          {index < breadcrumbPath.length - 1 && (
                            <span className="mx-1 text-gray-400">/</span>
                          )}
                        </div>
                      ));
                    })()}
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToMainChat}
                    className={isMobile ? 'h-7 w-7 p-0' : ''}
                    title={t('chat.allMessages', 'Alle Nachrichten')}
                  >
                    <Home className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowHelp(true)}
                    className={isMobile ? 'h-7 w-7 p-0' : ''}
                    title={t('help.title', 'Hilfe')}
                    data-testid="header-help-button"
                  >
                    <HelpCircle className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} />
                  </Button>
                  <NotificationBell userId={currentUser.id} />
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowUserProfile(true)}
                    className={isMobile ? 'h-7 w-7 p-0' : ''}
                  >
                    <Avatar className={isMobile ? 'w-5 h-5' : 'w-6 h-6'}>
                      <AvatarFallback className="bg-emerald-500 text-white text-xs">
                        {currentUser.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Tabs Container */}
            <div className="flex-1 flex flex-col bg-white min-h-0">
              {/* Tab Navigation */}
              <div className={`border-b bg-gray-50 flex-shrink-0 ${isMobile ? 'px-2 py-1' : 'px-4 py-2'}`}>
                <div className="flex justify-between items-center">
                  <div className="flex space-x-1">
                    <button
                      onClick={() => setActiveTab('chat')}
                      className={`text-sm font-medium rounded-lg transition-colors ${
                        isMobile ? 'px-2 py-1' : 'px-4 py-2'
                      } ${
                        activeTab === 'chat' 
                          ? 'bg-emerald-600 text-white' 
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <MessageCircle className={`inline-block mr-1 ${isMobile ? 'w-3 h-3' : 'w-4 h-4 mr-2'}`} />
                      {t('projects.chat')}
                    </button>
                    <button
                      onClick={() => setActiveTab('files')}
                      className={`text-sm font-medium rounded-lg transition-colors ${
                        isMobile ? 'px-2 py-1' : 'px-4 py-2'
                      } ${
                        activeTab === 'files' 
                          ? 'bg-emerald-600 text-white' 
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <FileText className={`inline-block mr-1 ${isMobile ? 'w-3 h-3' : 'w-4 h-4 mr-2'}`} />
                      {t('projects.files')}
                    </button>
                    <button
                      onClick={() => setActiveTab('tasks')}
                      className={`text-sm font-medium rounded-lg transition-colors ${
                        isMobile ? 'px-2 py-1' : 'px-4 py-2'
                      } ${
                        activeTab === 'tasks' 
                          ? 'bg-emerald-600 text-white' 
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <ClipboardList className={`inline-block mr-1 ${isMobile ? 'w-3 h-3' : 'w-4 h-4 mr-2'}`} />
                      {t('projects.tasks')}
                    </button>
                    <button
                      onClick={() => setActiveTab('data')}
                      className={`text-sm font-medium rounded-lg transition-colors ${
                        isMobile ? 'px-2 py-1' : 'px-4 py-2'
                      } ${
                        activeTab === 'data' 
                          ? 'bg-emerald-600 text-white' 
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <Table className={`inline-block mr-1 ${isMobile ? 'w-3 h-3' : 'w-4 h-4 mr-2'}`} />
                      {t('projects.data', 'Data')}
                    </button>
                  </div>
                  
                  <div className="flex space-x-1">
                    <button
                      onClick={() => setActiveTab('members')}
                      className={`rounded-lg transition-colors ${
                        isMobile ? 'p-1.5' : 'p-2'
                      } ${
                        activeTab === 'members' 
                          ? 'bg-emerald-600 text-white' 
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                      title={t('projects.team')}
                    >
                      <Users className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} />
                    </button>
                    <button
                      onClick={() => setActiveTab('info')}
                      className={`rounded-lg transition-colors ${
                        isMobile ? 'p-1.5' : 'p-2'
                      } ${
                        activeTab === 'info' 
                          ? 'bg-emerald-600 text-white' 
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                      title={t('projects.info')}
                    >
                      <Info className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} />
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Tab Content */}
              <div className="flex-1 min-h-0 overflow-hidden">
                {activeTab === 'chat' && (
                  <Chat 
                    project={currentProject} 
                    currentUser={currentUser} 
                    onFilePreview={openFilePreview}
                    scrollToMessage={scrollToMessage}
                    scrollBehavior={scrollBehavior}
                    onScrollComplete={() => {
                      setScrollToMessage(null);
                    }}
                  />
                )}
                {activeTab === 'members' && (
                  <div className="h-full overflow-y-auto">
                    <ProjectMembers 
                      project={currentProject} 
                      currentUser={currentUser} 
                      onProjectUpdated={() => {
                        // Reload project to get updated creator_id
                        loadUserProjects();
                      }}
                    />
                  </div>
                )}
                {activeTab === 'files' && (
                  <div className="h-full overflow-y-auto">
                    <ProjectFiles project={currentProject} currentUser={currentUser} onFilePreview={openFilePreview} />
                  </div>
                )}
                {activeTab === 'tasks' && (
                  <div className="h-full overflow-hidden flex flex-col">
                    <TaskList 
                      projectId={currentProject.id}
                      projectName={currentProject.name}
                      currentUser={currentUser}
                      projectMembers={projectMembers}
                    />
                  </div>
                )}
                {activeTab === 'data' && (
                  <div className="h-full overflow-y-auto">
                    <ProjectDataTables 
                      projectId={currentProject.id}
                      currentUser={currentUser}
                    />
                  </div>
                )}
                {activeTab === 'info' && (
                  <div className="h-full overflow-y-auto p-6">
                    <ProjectInfo 
                      project={currentProject} 
                      currentUser={currentUser} 
                      onProjectDeleted={() => {
                        setCurrentProject(null);
                        loadUserProjects();
                      }}
                      onProjectUpdated={(updatedProject) => {
                        setCurrentProject(updatedProject);
                        loadUserProjects();
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">{t('dashboard.welcome')}</p>
              <p>{t('dashboard.selectProject')}</p>
              {!sidebarVisible && (
                <Button
                  onClick={toggleSidebar}
                  variant="outline"
                  className="mt-4"
                >
                  📁 {t('dashboard.showProjects')}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* File Preview Modal */}
      <FilePreview
        isOpen={filePreview.isOpen}
        onClose={closeFilePreview}
        fileUrl={filePreview.fileUrl}
        fileName={filePreview.fileName}
        fileType={filePreview.fileType}
        originalFileUrl={filePreview.originalFileUrl}
      />

      {/* Import Modal (Excel/CSV) */}
      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        currentUser={currentUser}
        onImportComplete={(result) => {
          loadUserProjects();
          setShowImportModal(false);
        }}
      />

      {/* BCF Import Modal */}
      <BCFImportModal
        isOpen={showBCFImportModal}
        onClose={() => setShowBCFImportModal(false)}
        currentUser={currentUser}
        projectId={currentProject?.id}
        projectName={currentProject?.name}
        onImportComplete={(result) => {
          loadUserProjects();
          setShowBCFImportModal(false);
        }}
      />

      {/* Join Project with Code Modal */}
      {showJoinCodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold mb-2">🔗 {t('joinCode.title')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('joinCode.description')}
            </p>
            
            <div className="mb-4">
              <Input
                type="text"
                value={joinCode}
                onChange={(e) => {
                  setJoinCode(e.target.value.toUpperCase());
                  setJoinError('');
                }}
                placeholder={t('joinCode.placeholder')}
                className="text-center text-xl font-mono tracking-widest uppercase"
                maxLength={8}
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && joinWithCode()}
              />
              {joinError && (
                <p className="text-red-500 text-sm mt-2 text-center">{joinError}</p>
              )}
            </div>
            
            <div className="text-xs text-gray-500 mb-4 bg-gray-50 p-3 rounded">
              <p>💡 {t('joinCode.hint')}</p>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowJoinCodeModal(false);
                  setJoinCode('');
                  setJoinError('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={joinWithCode}
                disabled={joinLoading || !joinCode.trim()}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {joinLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  t('joinCode.join')
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* PWA Install Prompt */}
      <PWAInstall currentUser={currentUser} justRegistered={justRegistered} />
      </div>
    </div>
  );
};

export default Dashboard;
